import React, { useState, useEffect } from "react";
import * as Mui from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentLocation } from "services/Redux/userToken";
import { Visibility, VisibilityOff, CheckCircle, Cancel } from "@mui/icons-material";
import { changePassword } from "services/constants";
import { showToast } from "services/utils/Status";
import { useHistory } from "react-router-dom";
import { customAPI } from "services/utils/CustomAPI";
import {
  designColors,
  designFontFamily,
  designWeights,
  designRadii,
} from "services/constants/designTokens";
import { GlobalFieldLabel } from "components/DesignSystem/sharedUiPatterns";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";

const passwordFieldSx = {
  "& .MuiOutlinedInput-root": {
    fontFamily: designFontFamily,
    borderRadius: designRadii.control,
    backgroundColor: designColors.white,
  },
};

const RuleRow = ({ met, label }) => (
  <Mui.Stack direction="row" spacing={0.75} alignItems="center">
    {met ? (
      <CheckCircle sx={{ fontSize: 15, color: designColors.tealDark }} />
    ) : (
      <Cancel sx={{ fontSize: 15, color: met === "" ? designColors.slate300 : "#B42318" }} />
    )}
    <Mui.Typography
      sx={{
        fontSize: 12.5,
        fontFamily: designFontFamily,
        color: met ? designColors.tealDark : met === "" ? designColors.slate500 : "#B42318",
      }}
    >
      {label}
    </Mui.Typography>
  </Mui.Stack>
);

export const ChangePassword = ({ onStateChange, hideInlineButton = false }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  useEffect(() => {
    // Only for the standalone /admin/changepassword route. Skipped when
    // embedded in UserSidePeek's panel (hideInlineButton) — there, no real
    // navigation happened, so stomping the global currentLocation to
    // "ChangePassword" (a value no sidebar menu item matches) was silently
    // collapsing whichever section's submenu the user had open, since
    // Sidebar/index.js's isMainMenuExpanded requires
    // currentLocation === item.name to keep a submenu's <Collapse> open.
    if (!hideInlineButton) {
      dispatch(setCurrentLocation("ChangePassword"));
    }
  }, [dispatch, hideInlineButton]);
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [oldpassword, setOldPassword] = useState("");
  const [newpassword, setNewPassword] = useState("");
  const [confirmnewpassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showCnfmNewPassword, setShowCnfmNewPassword] = useState(false);
  const [passwordEqualCheck, setPasswordEqualCheck] = useState("");
  const [buttonValidation, setButtonValidation] = useState(true);
  const [lowercase, setLowercase] = useState("");
  const [uppercase, setUppercase] = useState("");
  const [number, setNumber] = useState("");
  const [passwordLength, setPasswordLength] = useState("");
  const [callApi, setCallApi] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);

  const handleClickShowNewPwd = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowCnfmNewPwd = () => {
    setShowCnfmNewPassword(!showCnfmNewPassword);
  };

  useEffect(() => {
    const changePasswordRequest = async () => {
      setButtonLoader(true);
      const response = await customAPI(`${domain}change-password/`, "POST", {
        old_password: oldpassword,
        new_password: newpassword,
      });
      if (response.ok) {
        setOldPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
        setPasswordEqualCheck("");
        showToast("success", changePassword.successAlert);
        setButtonValidation(true);
        setButtonLoader(false);
      } else {
        setButtonValidation(true);
        setButtonLoader(false);
      }
    };
    if (callApi) {
      changePasswordRequest();
      setCallApi(false);
    }
  }, [callApi, domain, history, newpassword, oldpassword, token]);

  const checkValidation = () => {
    if (
      confirmnewpassword !== "" &&
      oldpassword !== "" &&
      newpassword !== "" &&
      confirmnewpassword === newpassword &&
      newpassword.match(/[A-Z]/g) &&
      newpassword.match(/[A-Z]/g) &&
      newpassword.match(/[0-9]/g)
    ) {
      setButtonValidation(false);
    } else {
      setButtonValidation(true);
    }
  };

  const handleKeyDown = () => {
    newpassword.match(/[a-z]/g) ? setLowercase(true) : setLowercase(false);
    newpassword.match(/[A-Z]/g) ? setUppercase(true) : setUppercase(false);
    newpassword.match(/[0-9]/g) ? setNumber(true) : setNumber(false);
    newpassword.length >= 8
      ? setPasswordLength(true)
      : setPasswordLength(false);
    if (
      newpassword.match(/[a-z]/g) &&
      newpassword.match(/[A-Z]/g) &&
      newpassword.match(/[0-9]/g) &&
      newpassword.length >= 8
    ) {
      setLowercase("");
      setUppercase("");
      setNumber("");
      setPasswordLength("");
    }
  };

  const UpdatePassword = () => {
    setCallApi(true);
  };

  // Lets UserSidePeek drive an external sticky footer button (matching the
  // Approval Center create-screen pattern) when this is rendered inside the
  // panel instead of as the standalone /admin/changepassword route.
  useEffect(() => {
    onStateChange?.({
      buttonValidation,
      buttonLoader,
      submit: UpdatePassword,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonValidation, buttonLoader, onStateChange]);

  const visibilityAdornment = (visible, onClick) => ({
    endAdornment: (
      <Mui.InputAdornment position="end">
        <Mui.IconButton onClick={onClick} edge="end" size="small">
          {visible ? (
            <Visibility sx={{ fontSize: 19, color: designColors.slate500 }} />
          ) : (
            <VisibilityOff sx={{ fontSize: 19, color: designColors.slate500 }} />
          )}
        </Mui.IconButton>
      </Mui.InputAdornment>
    ),
  });

  return (
    <Mui.Box
      sx={
        hideInlineButton
          ? { px: 3, py: 2 }
          : { maxWidth: 460, mx: "auto", p: { xs: 2.5, md: 3 } }
      }
    >
      <Mui.Typography
        sx={{
          fontSize: 15,
          fontWeight: designWeights.semibold,
          fontFamily: designFontFamily,
          color: designColors.slate900,
          mb: 0.5,
        }}
      >
        Update your password
      </Mui.Typography>
      <Mui.Typography
        sx={{ fontSize: 13, fontFamily: designFontFamily, color: designColors.slate600, mb: 3 }}
      >
        Use a strong password you don&apos;t use elsewhere.
      </Mui.Typography>

      <Mui.Stack spacing={2.5}>
        <Mui.Box>
          <GlobalFieldLabel helperText="Enter your existing account password">
            Current password
          </GlobalFieldLabel>
          <Mui.TextField
            fullWidth
            sx={passwordFieldSx}
            value={oldpassword}
            placeholder="Enter current password"
            type="password"
            onKeyUp={checkValidation}
            onChange={(e) => setOldPassword(e.target.value)}
            inputProps={{ autoComplete: "new-password" }}
          />
        </Mui.Box>

        <Mui.Box>
          <GlobalFieldLabel helperText="Must meet all the requirements listed below">
            New password
          </GlobalFieldLabel>
          <Mui.TextField
            fullWidth
            sx={passwordFieldSx}
            value={newpassword}
            placeholder="Enter new password"
            type={showNewPassword ? "text" : "password"}
            onKeyUpCapture={handleKeyDown}
            onChange={(e) => setNewPassword(e.target.value)}
            onKeyUp={checkValidation}
            autoComplete="off"
            InputProps={visibilityAdornment(showNewPassword, handleClickShowNewPwd)}
          />
          <Mui.Stack spacing={0.5} sx={{ mt: 1 }}>
            <RuleRow met={lowercase} label="A lowercase letter" />
            <RuleRow met={uppercase} label="An uppercase letter" />
            <RuleRow met={number} label="A number" />
            <RuleRow met={passwordLength} label="Minimum 8 characters" />
          </Mui.Stack>
        </Mui.Box>

        <Mui.Box>
          <GlobalFieldLabel helperText="Re-enter the new password to confirm">
            Confirm new password
          </GlobalFieldLabel>
          <Mui.TextField
            fullWidth
            sx={passwordFieldSx}
            value={confirmnewpassword}
            placeholder="Re-enter new password"
            type={showCnfmNewPassword ? "text" : "password"}
            onChange={(e) => {
              if (e.target.value === newpassword) {
                setPasswordEqualCheck(false);
              } else {
                setPasswordEqualCheck(true);
              }
              setConfirmNewPassword(e.target.value);
            }}
            onKeyUp={checkValidation}
            autoComplete="off"
            InputProps={visibilityAdornment(showCnfmNewPassword, handleClickShowCnfmNewPwd)}
          />
          {passwordEqualCheck === "" ? null : (
            <Mui.Typography
              sx={{
                mt: 0.5,
                fontSize: 12.5,
                fontFamily: designFontFamily,
                color: passwordEqualCheck ? "#B42318" : designColors.tealDark,
              }}
            >
              {passwordEqualCheck ? "Passwords don't match" : "Passwords match"}
            </Mui.Typography>
          )}
        </Mui.Box>

        {hideInlineButton ? null : (
          <PrimaryButton
            onClick={UpdatePassword}
            disabled={buttonValidation || buttonLoader}
            loading={buttonLoader}
          >
            Set Password
          </PrimaryButton>
        )}
      </Mui.Stack>
    </Mui.Box>
  );
};
